import * as React from 'react'
import { useMemo } from 'react'
import { Button, Drawer, FormLayoutRow, Well } from '@pushly/aqe/lib/components'
import {
    CaretDownFilled,
    CaretUpFilled,
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    InfoCircleFilled,
    InfoCircleOutlined,
    PlusOutlined,
    RightOutlined,
} from '@ant-design/icons'
import { Alert, Collapse, Flex, Form, Input, InputNumber, Popover, Select, Skeleton, Tooltip } from 'antd'
import type { Rule } from 'antd/es/form'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { AppState } from '@halo/stores/app.state'
import { Container } from 'typescript-ioc/es5'
import * as randomstring from 'randomstring'
import deepEqual from 'fast-deep-equal'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asDomainSubject } from '@halo/stores/ability.state'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import {
    CoordinateSource,
    DEFAULT_CELL_RESOLUTION,
    DEFAULT_COORDINATES_FIELD,
    DEFAULT_COORDINATE_PRECISION,
    DomainGeography,
    DomainGeographyLike,
    MAX_CELL_RESOLUTION,
    MAX_COORDINATE_PRECISION,
    MIN_CELL_RESOLUTION,
    MIN_COORDINATE_PRECISION,
    asDomainGeography,
} from '@pushly/models/lib/structs/domains'
import {
    GEO_RESOLUTION_PRECISION_ORDER,
    GeoResolution,
    geoResolutionPermits,
} from '@pushly/models/lib/enums/geo-resolution'
import { PropertiesOf } from '@pushly/models/lib/types/properties-of'

/**
 * A coordinate source as the form holds it: a plain object, since antd field values are edited in
 * place rather than reconstructed as structs on every keystroke.
 */
type CoordinateSourceInput = PropertiesOf<CoordinateSource>

/**
 * The resolution levels in the plain language the people who set this actually use. Account managers
 * configure a domain's geography without knowing our enum names, so the enum member never reaches
 * the screen - `description` says what the domain keeps, which is the question being answered.
 *
 * Ordered least to most detail, matching the enum, so the control reads as a ladder.
 */
export const GEO_RESOLUTION_LABELS: Record<GeoResolution, { label: string; description: string }> = {
    [GeoResolution.NONE]: {
        label: 'No location',
        description: 'Nothing about where a subscriber is. Only their time zone is kept.',
    },
    [GeoResolution.COUNTRY]: {
        label: 'Country',
        description: 'Country and continent only. Targeting narrower than a country is unavailable.',
    },
    [GeoResolution.PROVINCE]: {
        label: 'State or region',
        description: 'Down to the state, province or region, plus everything coarser.',
    },
    [GeoResolution.CITY]: {
        label: 'City',
        description: 'Down to the city, plus state and country.',
    },
    [GeoResolution.POSTAL_CODE]: {
        label: 'Postal code',
        description: 'Down to the postal code, plus city, state and country.',
    },
    [GeoResolution.COORDINATES]: {
        label: 'Precise coordinates',
        description: 'Tracks latitude, longitude, and all other coarse location detail.',
    },
}

export const GEO_RESOLUTION_OPTIONS = GEO_RESOLUTION_PRECISION_ORDER.map((resolution) => ({
    value: resolution,
    label: GEO_RESOLUTION_LABELS[resolution].label,
}))

export function describeGeoResolution(resolution: GeoResolution | undefined): string {
    return resolution && GEO_RESOLUTION_LABELS[resolution] ? GEO_RESOLUTION_LABELS[resolution].label : 'Not set'
}

export function explainGeoResolution(resolution: GeoResolution | undefined): string {
    return resolution && GEO_RESOLUTION_LABELS[resolution] ? GEO_RESOLUTION_LABELS[resolution].description : ''
}

/**
 * A field label carrying the help marker used across the domain settings wells - the field name
 * followed by an info icon that reveals its help text on hover.
 *
 * Help text hangs off the label rather than sitting under the control so that reading it never
 * changes the height of the form.
 */
const FieldLabelWithHelpPopover: React.FC<{ help: React.ReactNode; children: React.ReactNode }> = ({
    help,
    children,
}) => (
    <span>
        {children}
        <Popover classNames={{ content: 'geography-field-help' }} content={help}>
            <InfoCircleOutlined className="info-icon" />
        </Popover>
    </span>
)

/**
 * Parses a geography out of whatever the DTO is carrying. The API returns snake_case and a domain
 * patched during this session is merged back in straight off the wire, so this normalizes rather
 * than assuming it was handed an already-hydrated struct.
 *
 * A domain with no geography at all reads as country - the column is mandatory and defaults to it,
 * so the only way to see nothing here is a partial record, and country is the level such a record
 * would be sitting at.
 */
export function toGeography(geography: DomainGeographyLike | null | undefined): DomainGeography {
    return asDomainGeography(geography ?? { resolution: GeoResolution.COUNTRY })
}

/**
 * The values the form starts with for a domain, including the seeded coordinate index parameters a
 * domain that has never indexed coordinates needs. Seeding them here rather than in the advanced
 * fields themselves means an account manager switching to coordinate resolution has a working
 * configuration without opening the advanced section at all.
 */
export function initialGeographyFormValues(geography: DomainGeography) {
    const coordinates = geography.coordinates

    return {
        resolution: geography.resolution,
        coordinatePrecision: coordinates?.coordinatePrecision ?? DEFAULT_COORDINATE_PRECISION,
        cellResolution: coordinates?.cellResolution ?? DEFAULT_CELL_RESOLUTION,
        sources: (coordinates?.sources ?? [{ coordinatesField: DEFAULT_COORDINATES_FIELD }]).map((source) => ({
            ...source,
        })),
    }
}

/**
 * The geography a set of form values describes, in the snake_case form the API takes.
 *
 * The coordinate block is assembled only at coordinate resolution. Whatever the advanced fields
 * still hold from an earlier selection is dropped rather than sent: index parameters at a resolution
 * that stores no coordinate would name a keyspace nothing ever writes to. The server rejects that
 * combination, but the form must not be able to build one either, which is why this is the single
 * place a payload is constructed.
 *
 * `version` is absent by design. It scopes an index keyspace that never expires, so it is derived
 * where the config is persisted and a value sent from here would be discarded.
 */
export function buildGeographyPayload(values: any): Record<string, any> {
    const resolution = values?.resolution

    if (resolution !== GeoResolution.COORDINATES) {
        return { resolution }
    }

    return {
        resolution,
        coordinates: {
            coordinate_precision: values.coordinatePrecision,
            cell_resolution: values.cellResolution,
            sources: (values.sources ?? []).map((source: CoordinateSourceInput) =>
                CoordinateSource.parseJsonObject(source).serialize(),
            ),
        },
    }
}

export function describeSource(source: CoordinateSourceInput): string {
    if (source.coordinatesField !== undefined) {
        return source.coordinatesField || '(unset)'
    }

    return `${source.latitudeField || '(unset)'} / ${source.longitudeField || '(unset)'}`
}

/**
 * Swaps a source with its up/down neighbor, or returns the list unchanged when the move would
 * go out of bounds (moving the first source up, or the last one down).
 */
export function moveSource(
    sources: CoordinateSourceInput[],
    index: number,
    direction: 'up' | 'down',
): CoordinateSourceInput[] {
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= sources.length) {
        return sources
    }

    const next = [...sources]
    ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]
    return next
}

interface IGeographySourcesEditorProps {
    value?: CoordinateSourceInput[]
    onChange?: (value: CoordinateSourceInput[]) => void
    disabled?: boolean
}

/**
 * Sources are tried in the order shown here, top to bottom - the first one that yields a
 * complete coordinate for a subscriber wins. That makes order meaningful, so rows are
 * reordered with explicit up/down controls rather than presented as an unordered list.
 */
const GeographySourcesEditor: React.FC<IGeographySourcesEditorProps> = ({ value, onChange, disabled }) => {
    const sources = value ?? []

    const emit = (next: CoordinateSourceInput[]) => onChange?.(next)

    const handleAdd = () => {
        emit([...sources, { coordinatesField: '' }])
    }

    const handleDelete = (index: number) => {
        emit(sources.filter((_, idx) => idx !== index))
    }

    const handleMove = (index: number, direction: 'up' | 'down') => {
        emit(moveSource(sources, index, direction))
    }

    const handleFormChange = (index: number, form: 'array' | 'pair') => {
        const next = [...sources]
        next[index] = form === 'array' ? { coordinatesField: '' } : { latitudeField: '', longitudeField: '' }
        emit(next)
    }

    const handleFieldChange = (index: number, field: keyof CoordinateSourceInput, fieldValue: string) => {
        const next = [...sources]
        next[index] = { ...next[index], [field]: fieldValue }
        emit(next)
    }

    // Reordering is shown only once there is an order to change, which is also what keeps a
    // single-source list from carrying a column of permanently disabled buttons.
    const reorderable = sources.length > 1

    return (
        <Flex vertical gap={8} className="geography-sources-editor">
            {sources.map((source, idx) => {
                const form = source.coordinatesField !== undefined ? 'array' : 'pair'

                return (
                    <Flex key={idx} gap={8} align="center" className="geography-source-row">
                        {reorderable && (
                            <Flex vertical className="geography-source-row-reorder">
                                <Tooltip title="Move earlier">
                                    <span>
                                        <Button
                                            type="text"
                                            size="small"
                                            aria-label={`Move source ${idx + 1} earlier`}
                                            icon={<CaretUpFilled />}
                                            disabled={disabled || idx === 0}
                                            onClick={() => handleMove(idx, 'up')}
                                        />
                                    </span>
                                </Tooltip>
                                <Tooltip title="Move later">
                                    <span>
                                        <Button
                                            type="text"
                                            size="small"
                                            aria-label={`Move source ${idx + 1} later`}
                                            icon={<CaretDownFilled />}
                                            disabled={disabled || idx === sources.length - 1}
                                            onClick={() => handleMove(idx, 'down')}
                                        />
                                    </span>
                                </Tooltip>
                            </Flex>
                        )}

                        <span className="geography-source-row-index">{idx + 1}</span>

                        <Select<'array' | 'pair'>
                            value={form}
                            onChange={(f) => handleFormChange(idx, f)}
                            disabled={disabled}
                            className="geography-source-row-form"
                            options={[
                                { value: 'array', label: 'Coordinates field' },
                                { value: 'pair', label: 'Latitude / longitude fields' },
                            ]}
                        />

                        {form === 'array' ? (
                            <Input
                                value={source.coordinatesField ?? ''}
                                onChange={(e) => handleFieldChange(idx, 'coordinatesField', e.target.value)}
                                disabled={disabled}
                                placeholder="e.g. location"
                                className="geography-source-row-field"
                            />
                        ) : (
                            <>
                                <Input
                                    value={source.latitudeField ?? ''}
                                    onChange={(e) => handleFieldChange(idx, 'latitudeField', e.target.value)}
                                    disabled={disabled}
                                    placeholder="latitude field"
                                    className="geography-source-row-field"
                                />
                                <Input
                                    value={source.longitudeField ?? ''}
                                    onChange={(e) => handleFieldChange(idx, 'longitudeField', e.target.value)}
                                    disabled={disabled}
                                    placeholder="longitude field"
                                    className="geography-source-row-field"
                                />
                            </>
                        )}

                        <Tooltip title="Remove source">
                            <span>
                                <Button
                                    type="text"
                                    size="small"
                                    aria-label={`Remove source ${idx + 1}`}
                                    className="geography-source-row-remove"
                                    icon={<DeleteOutlined />}
                                    disabled={disabled || sources.length <= 1}
                                    onClick={() => handleDelete(idx)}
                                />
                            </span>
                        </Tooltip>
                    </Flex>
                )
            })}

            {/* Indented onto the row's first control so Add Source lines up with the selects above
                it, in both the single- and multi-source states. */}
            <Flex className="geography-sources-add" data-reorderable={reorderable}>
                <Button type="dashed" size="small" icon={<PlusOutlined />} disabled={disabled} onClick={handleAdd}>
                    Add Source
                </Button>
            </Flex>
        </Flex>
    )
}

export const validateSources = async (_rule: Rule, value: CoordinateSourceInput[] | undefined) => {
    if (!Array.isArray(value) || value.length === 0) {
        return Promise.reject('At least one coordinate source is required.')
    }

    for (let idx = 0; idx < value.length; idx++) {
        const source = value[idx]
        const hasCoordinatesField = !!source.coordinatesField
        const hasLatitudeField = !!source.latitudeField
        const hasLongitudeField = !!source.longitudeField

        if (hasLatitudeField !== hasLongitudeField) {
            return Promise.reject(`Source ${idx + 1} needs both latitude and longitude fields, or neither.`)
        }

        if (hasCoordinatesField === hasLatitudeField) {
            return Promise.reject(
                `Source ${idx + 1} needs exactly one of a coordinates field or a latitude/longitude pair.`,
            )
        }
    }

    return Promise.resolve()
}

export const validateCoordinatePrecision = async (_rule: Rule, value: number | undefined) =>
    typeof value === 'number' && value >= MIN_COORDINATE_PRECISION && value <= MAX_COORDINATE_PRECISION
        ? Promise.resolve()
        : Promise.reject(
              `Coordinate precision must be a whole number between ${MIN_COORDINATE_PRECISION} and ${MAX_COORDINATE_PRECISION}.`,
          )

export const validateCellResolution = async (_rule: Rule, value: number | undefined) =>
    typeof value === 'number' && value >= MIN_CELL_RESOLUTION && value <= MAX_CELL_RESOLUTION
        ? Promise.resolve()
        : Promise.reject(
              `Cell resolution must be a whole number between ${MIN_CELL_RESOLUTION} and ${MAX_CELL_RESOLUTION}.`,
          )

type DrawerCloseEvent = React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>

interface IDomainGeographyEditorProps {
    domain: DomainDto
    visible: boolean
    onClose: (e?: DrawerCloseEvent) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

export const DomainGeographyEditor: React.FC<IDomainGeographyEditorProps> = ({
    domain,
    visible,
    onClose,
    onDomainUpdated,
}) => {
    const domainService = useMemo(() => Container.get(DomainService), [])

    const [form] = Form.useForm()
    // Re-render on any field change so the selected level's explanation and disableSubmit stay current.
    Form.useWatch([], form)

    const previousGeography = toGeography(domain.geography)
    const initialValues = useMemo(() => initialGeographyFormValues(previousGeography), [domain.geography])

    const selectedResolution: GeoResolution = form.getFieldValue('resolution') ?? initialValues.resolution
    const indexesCoordinates = selectedResolution === GeoResolution.COORDINATES

    // Compared against the payload rather than the form values: only what would actually be sent
    // counts as a change, so editing an advanced field the current resolution ignores is not one.
    const hasChanges = !deepEqual(
        buildGeographyPayload({ ...initialValues, ...form.getFieldsValue() }),
        buildGeographyPayload(initialValues),
    )

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()

            const { ok, data } = await domainService.patch(
                domain.id,
                { geography: buildGeographyPayload({ ...initialValues, ...values }) },
                { showLoadingScreen: true },
            )

            if (ok) {
                onDomainUpdated(data)
                onClose()
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            title="Update Geography Settings"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleSubmit}
            disableSubmit={!hasChanges}
            onClose={onClose}
            open={visible}
        >
            <Form
                form={form}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                initialValues={initialValues}
                className="hide-inline-errors"
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well mode="ghost" hideHeader={true} footerMode="hidden" padding="none">
                    <Form.Item
                        name="resolution"
                        rules={[{ required: true, message: 'A level of location detail is required.' }]}
                        label={
                            <FieldLabelWithHelpPopover help="How precisely subscriber location is stored.">
                                Location Detail
                            </FieldLabelWithHelpPopover>
                        }
                        extra={explainGeoResolution(selectedResolution)}
                    >
                        <Select<GeoResolution> options={GEO_RESOLUTION_OPTIONS} />
                    </Form.Item>

                    {indexesCoordinates && (
                        <>
                            <Collapse
                                ghost={true}
                                className="geography-advanced-settings"
                                expandIcon={({ isActive }) => (isActive ? <DownOutlined /> : <RightOutlined />)}
                                items={[
                                    {
                                        key: 'advanced',
                                        label: 'Advanced',
                                        // Rendered whether or not the section is open, so the seeded
                                        // values below are registered and validated either way. An
                                        // account manager who never opens this must still submit a
                                        // working coordinate configuration.
                                        forceRender: true,
                                        children: (
                                            <>
                                                <Form.Item
                                                    name="coordinatePrecision"
                                                    rules={[{ required: true, validator: validateCoordinatePrecision }]}
                                                    validateTrigger="onBlur"
                                                    label={
                                                        <FieldLabelWithHelpPopover help="Decimal places kept on stored coordinates.">
                                                            Coordinate Precision
                                                        </FieldLabelWithHelpPopover>
                                                    }
                                                >
                                                    <InputNumber
                                                        min={MIN_COORDINATE_PRECISION}
                                                        max={MAX_COORDINATE_PRECISION}
                                                        precision={0}
                                                        className="geography-numeric-field"
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    name="cellResolution"
                                                    rules={[{ required: true, validator: validateCellResolution }]}
                                                    validateTrigger="onBlur"
                                                    label={
                                                        <FieldLabelWithHelpPopover help="H3 cell resolution used for coarse geo matching.">
                                                            Cell Resolution
                                                        </FieldLabelWithHelpPopover>
                                                    }
                                                >
                                                    <InputNumber
                                                        min={MIN_CELL_RESOLUTION}
                                                        max={MAX_CELL_RESOLUTION}
                                                        precision={0}
                                                        className="geography-numeric-field"
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    name="sources"
                                                    rules={[{ validator: validateSources }]}
                                                    label={
                                                        <FieldLabelWithHelpPopover help="Where coordinates are read from on a subscriber profile, in order.">
                                                            Coordinate Sources
                                                        </FieldLabelWithHelpPopover>
                                                    }
                                                >
                                                    <GeographySourcesEditor />
                                                </Form.Item>
                                            </>
                                        ),
                                    },
                                ]}
                            />

                            <Alert
                                className="geography-rebuild-notice"
                                role="status"
                                icon={<InfoCircleFilled />}
                                showIcon={true}
                                title="Saving queues a background index rebuild. Until it completes, this domain keeps resolving geo segments through Snowflake."
                            />
                        </>
                    )}
                </Well>
            </Form>
        </Drawer>
    )
}

interface IDomainGeographyWellProps {
    loading?: boolean
    domain: DomainDto
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

export const DomainGeographyWell: React.FC<IDomainGeographyWellProps> = ({ loading, domain, onDomainUpdated }) => {
    const [showDrawer, setShowDrawer] = React.useState(false)
    const [drawerKey, setDrawerKey] = React.useState<string | undefined>()

    const appState = React.useMemo(() => Container.get(AppState), [])

    const handleDrawerOpen = React.useCallback(() => {
        setShowDrawer(true)
        setDrawerKey(randomstring.generate())
    }, [])

    const handleDrawerClose = React.useCallback(() => {
        setShowDrawer(false)
    }, [])

    const geography = toGeography(domain?.geography)
    const coordinates = geoResolutionPermits(geography.resolution, GeoResolution.COORDINATES)
        ? geography.coordinates
        : undefined

    const canUpdate = appState.abilityStore.can(AbilityAction.UPDATE, asDomainSubject(domain))

    return (
        <Well
            className="type-3 nested"
            title="Geography Settings"
            actions={
                canUpdate && (
                    <Button size="small" onClick={handleDrawerOpen} disabled={loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                )
            }
            hideFooter={true}
        >
            <Skeleton loading={loading} active={true} title={false}>
                {!loading && (
                    <>
                        <Well mode="ghost" hideHeader={true} hideFooter={true}>
                            <FormLayoutRow
                                label="Location Detail"
                                value={describeGeoResolution(geography.resolution)}
                            />
                            {coordinates && (
                                <>
                                    <FormLayoutRow
                                        label="Coordinate Precision"
                                        value={String(coordinates.coordinatePrecision)}
                                    />
                                    <FormLayoutRow label="Cell Resolution" value={String(coordinates.cellResolution)} />
                                    <FormLayoutRow
                                        label="Sources"
                                        value={coordinates.sources.map(describeSource).join(', ')}
                                    />
                                </>
                            )}
                        </Well>

                        <DomainGeographyEditor
                            key={drawerKey}
                            visible={showDrawer}
                            domain={domain}
                            onClose={handleDrawerClose}
                            onDomainUpdated={onDomainUpdated}
                        />
                    </>
                )}
            </Skeleton>
        </Well>
    )
}
